import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { toast } from "sonner";
import { Play, Pause, Lock, Gift, Download, ArrowLeft, MessageCircle, Star } from "lucide-react";
import RatingStars from "@/components/RatingStars";
import CommentThread from "@/components/CommentThread";

export default function EpisodeDetail() {
  const { number } = useParams();
  const epNum = parseInt(number, 10);
  const { user, shareStatus, redeemShare } = useAuth();
  const { current, playing, play, toggle } = usePlayer();
  const nav = useNavigate();

  const [ep, setEp] = useState(null);
  const [social, setSocial] = useState({ rating: { avg: 0, count: 0 }, my_rating: null, comments: [], comments_count: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const isCurrent = current?.number === epNum;

  const load = useCallback(async () => {
    try {
      const [epRes, socialRes] = await Promise.all([
        api.get(`/episodes/${epNum}`),
        api.get(`/episodes/${epNum}/social`),
      ]);
      setEp(epRes.data);
      setSocial(socialRes.data);
    } catch (e) {
      toast.error("could not load episode");
    } finally { setLoading(false); }
  }, [epNum]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const handlePlay = () => {
    if (!ep?.unlocked) return;
    if (isCurrent) toggle();
    else play(ep);
  };

  const handleUnlock = async () => {
    if (!user) { toast.error("sign up first"); nav("/signup"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/episode", { episode_number: epNum, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "checkout failed");
      setBusy(false);
    }
  };

  const handleShareUnlock = async () => {
    setBusy(true);
    try {
      await redeemShare(epNum);
      toast.success("unlocked ♡");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not redeem");
    } finally { setBusy(false); }
  };

  const handleRate = async (stars) => {
    if (!user) { toast.error("log in to rate"); return; }
    try {
      const { data } = await api.post(`/episodes/${epNum}/rate`, { stars });
      setSocial(s => ({ ...s, rating: { avg: data.avg, count: data.count }, my_rating: stars }));
      toast.success(`rated ${stars} star${stars > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error("could not rate");
    }
  };

  const handleDownload = async () => {
    if (!user || !ep?.unlocked) return;
    setDownloading(true);
    const t = toast.loading("preparing your watermarked copy...");
    try {
      const res = await api.get(`/episodes/${epNum}/download`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `if-we-knew-ep${String(epNum).padStart(2,"0")}.mp3`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("downloaded ♡");
    } catch (e) {
      toast.error("download failed");
    } finally { toast.dismiss(t); setDownloading(false); }
  };

  if (loading) return <div className="bg-ink min-h-screen text-white/50 font-outfit px-5 py-20 text-center has-player">loading...</div>;
  if (!ep) return <div className="bg-ink min-h-screen text-white/50 font-outfit px-5 py-20 text-center has-player">not found.</div>;

  const showPause = isCurrent && playing;
  const canRedeemShare = !ep.unlocked && !ep.is_free && shareStatus?.available;

  return (
    <div className="bg-ink min-h-screen has-player" data-testid={`episode-detail-${epNum}`}>
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-14">
        <Link to="/episodes" className="text-xs uppercase tracking-widest font-outfit text-white/60 hover:text-hotpink flex items-center gap-1 mb-6">
          <ArrowLeft size={14}/> all episodes
        </Link>

        {/* hero */}
        <div className="bg-coal border border-white/10 p-6 md:p-8 mb-8">
          <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="scribble text-hotpink text-xl mb-1 rotate-[-1deg] inline-block">ep {String(epNum).padStart(2,"0")} · {ep.recap_note}</p>
              <h1 className="brush-title text-4xl md:text-6xl text-white leading-[0.95]">{ep.title}</h1>
              <p className="text-white/70 font-outfit mt-3 text-base md:text-lg max-w-2xl">{ep.description}</p>
              <p className="text-white/40 text-xs uppercase tracking-widest mt-3">{ep.duration_label} · audio</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap mt-4">
            {ep.unlocked ? (
              <>
                <button onClick={handlePlay} data-testid="detail-play-btn" className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-6 py-3 shadow-[4px_4px_0px_#FFFFFF] hover:shadow-[6px_6px_0px_#FFFFFF] transition-all flex items-center gap-2">
                  {showPause ? <><Pause size={14} fill="white"/> pause</> : <><Play size={14} fill="white"/> play</>}
                </button>
                {user && (
                  <button onClick={handleDownload} disabled={downloading} data-testid="detail-download-btn" className="border border-white/30 hover:border-hotpink hover:text-hotpink text-white font-outfit font-bold uppercase tracking-widest text-xs px-5 py-3 flex items-center gap-2 transition-colors disabled:opacity-60">
                    <Download size={14}/> {downloading ? "..." : "download"}
                  </button>
                )}
              </>
            ) : (
              <>
                <button onClick={handleUnlock} disabled={busy} data-testid="detail-unlock-btn" className="bg-white text-ink hover:bg-hotpink hover:text-white font-outfit font-extrabold uppercase tracking-widest text-xs px-6 py-3 shadow-[4px_4px_0px_#FF007F] transition-all flex items-center gap-2">
                  <Lock size={14}/> unlock for $1
                </button>
                {canRedeemShare && (
                  <button onClick={handleShareUnlock} disabled={busy} className="border border-hotpink hover:bg-hotpink text-hotpink hover:text-white font-outfit font-bold uppercase tracking-widest text-xs px-5 py-3 flex items-center gap-2 transition-colors">
                    <Gift size={14}/> use free unlock
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Rating */}
        <div className="bg-coal border border-white/10 p-5 md:p-6 mb-8" data-testid="rating-section">
          <div className="flex items-start justify-between gap-5 flex-wrap">
            <div>
              <p className="scribble text-hotpink text-xl rotate-[-1deg] inline-block mb-1">your verdict</p>
              <h2 className="font-outfit font-bold text-white text-xl">Rate this episode</h2>
              {user ? (
                <div className="flex items-center gap-3 mt-3">
                  <RatingStars
                    value={social.my_rating || 0}
                    interactive
                    size={28}
                    onRate={handleRate}
                    data-testid="my-rating"
                  />
                  {social.my_rating && (
                    <span className="text-white/60 font-outfit text-sm">you rated {social.my_rating}/5</span>
                  )}
                </div>
              ) : (
                <p className="text-white/50 font-outfit text-sm mt-2"><Link to="/login" className="text-hotpink hover:underline">log in</Link> to leave a rating.</p>
              )}
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                <Star size={20} className="text-hotpink" fill="currentColor"/>
                <span className="brush-title text-4xl text-white leading-none">{social.rating.count > 0 ? social.rating.avg.toFixed(1) : "—"}</span>
              </div>
              <p className="text-white/40 text-xs uppercase tracking-widest font-outfit mt-1">{social.rating.count} rating{social.rating.count !== 1 ? "s" : ""}</p>
            </div>
          </div>
        </div>

        {/* Comments */}
        <div data-testid="comments-section">
          <div className="flex items-center gap-2 mb-5">
            <MessageCircle className="text-hotpink" size={20}/>
            <h2 className="brush-title text-3xl md:text-4xl text-white">THE <span className="text-hotpink">THREAD.</span></h2>
            <span className="text-white/40 text-sm font-outfit">({social.comments_count})</span>
          </div>
          <CommentThread episodeNumber={epNum} comments={social.comments} onChanged={load} />
        </div>
      </div>
    </div>
  );
}
